import {
  buildQueryArraySorters,
  SubtaskResourceField,
  useSubtaskEdit,
  useSubtaskList,
  useSubtaskOne,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { Loader, NoData } from '@sib/ui';
import { SubtaskCard } from './SubtaskCard';
import { SubtaskForm } from './SubtaskForm';

interface TaskSubtasksTabContentProps {
  taskId: string;
  canEdit: boolean;
  isAdding: boolean;
  editingSubtaskId: string | null;
  onEditSubtask: (subtaskId: string) => void;
  onGoBack: () => void;
}

export const TaskSubtasksTabContent = ({
  taskId,
  canEdit,
  isAdding,
  editingSubtaskId,
  onEditSubtask,
  onGoBack,
}: TaskSubtasksTabContentProps) => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching } = useSubtaskList(taskId, {
    page: DEFAULT_PAGE,
    size: MAXIMUM_PAGINATION,
    sort: buildQueryArraySorters([
      { field: SubtaskResourceField.CREATED_AT, order: 'desc' },
    ]),
  });
  const { mutate: updateSubtask, isPending: isUpdating } = useSubtaskEdit();
  const subtaskId = canEdit && editingSubtaskId ? editingSubtaskId : undefined;
  const { data: editingSubtask, isLoading: isLoadingEditingSubtask } =
    useSubtaskOne(taskId, subtaskId);
  const cannotEditTooltip = t('components.taskDetails.cannotEditTooltip');

  if (isAdding) return <SubtaskForm taskId={taskId} onGoBack={onGoBack} />;

  if (subtaskId) {
    if (isLoadingEditingSubtask || !editingSubtask)
      return (
        <div className="flex w-full justify-center py-6">
          <Loader className="size-6" />
        </div>
      );

    return (
      <SubtaskForm
        taskId={taskId}
        subtask={editingSubtask}
        onGoBack={onGoBack}
      />
    );
  }

  if (isLoading)
    return (
      <div className="flex w-full justify-center py-6">
        <Loader className="size-6" />
      </div>
    );

  const subtasks = data?.items ?? [];

  if (subtasks.length === 0)
    return (
      <NoData
        title={t('components.taskDetails.subtasks.emptyListMessage')}
        className="py-10"
      />
    );

  return (
    <div className="flex w-full flex-col gap-3 pr-1">
      {subtasks.map(subtask => (
        <SubtaskCard
          key={subtask.uuid}
          subtask={subtask}
          canEdit={canEdit}
          disabled={isUpdating || isFetching}
          disabledTooltip={cannotEditTooltip}
          editLabel={t('components.taskDetails.subtasks.card.edit')}
          onEdit={() => onEditSubtask(subtask.uuid)}
          onToggle={() =>
            updateSubtask({
              taskUuid: taskId,
              uuid: subtask.uuid,
              [SubtaskResourceField.COMPLETED]: !subtask.completed,
            })
          }
        />
      ))}
    </div>
  );
};
